/**
 * TEMP-H-016: Theme Registry
 * 
 * Registry of available themes with metadata.
 */

import type { ThemeId, ThemeMetadata, ThemeChangeDetail } from './types';
import { whenAuthReady, isAuthReady } from '@/lib/auth-readiness';

/**
 * Registry of all available themes
 */
export const THEME_REGISTRY: Record<ThemeId, ThemeMetadata> = {
  agi: {
    id: 'agi',
    name: 'AGI',
    description: 'The signature HI-SI theme with electric blue accents and deep space backgrounds.',
    author: 'HI-SI Design Team',
    version: '1.0.0',
    preview: {
      bg: '#0b0f14',
      accent: '#66e0ff',
      text: '#e6edf3'
    }
  },
  dark: {
    id: 'dark',
    name: 'Dark',
    description: 'A softer, more neutral dark theme with subtle blue accents.',
    author: 'HI-SI Design Team',
    version: '1.0.0',
    preview: {
      bg: '#0b0b0c',
      accent: '#8ab4ff',
      text: '#f5f6f8'
    }
  }
} as const;

/**
 * Default theme ID
 */
export const DEFAULT_THEME: ThemeId = 'agi';

/**
 * Available theme IDs
 */
export const AVAILABLE_THEMES: ThemeId[] = Object.keys(THEME_REGISTRY) as ThemeId[];

/**
 * Storage key for persisting theme preference
 */
export const THEME_STORAGE_KEY = 'ui.activeTheme';

/**
 * Custom event name for theme changes
 */
export const THEME_CHANGE_EVENT = 'theme:change';

/**
 * Get metadata for a specific theme
 */
export function getThemeMetadata(themeId: ThemeId): ThemeMetadata {
  const metadata = THEME_REGISTRY[themeId];
  if (!metadata) {
    console.warn(`Theme "${themeId}" not found in registry, falling back to default`);
    return THEME_REGISTRY[DEFAULT_THEME];
  }
  return metadata;
}

/**
 * Get metadata for all available themes
 */
export function getAllThemeMetadata(): ThemeMetadata[] {
  return AVAILABLE_THEMES.map(id => THEME_REGISTRY[id]);
}

/**
 * Get list of available theme IDs (for Settings UI)
 */
export function getAvailableThemes(): ThemeId[] {
  return [...AVAILABLE_THEMES]; // Return copy to prevent mutation
}

/**
 * Validate if a theme ID is supported
 */
export function isValidThemeId(themeId: string): themeId is ThemeId {
  return AVAILABLE_THEMES.includes(themeId as ThemeId);
}

/**
 * Get the saved theme from storage or return default
 */
export function getSavedTheme(storageKey = THEME_STORAGE_KEY): ThemeId {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved && isValidThemeId(saved)) {
      return saved;
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
  }
  return DEFAULT_THEME;
}

/**
 * Save theme preference to storage with quota handling
 * DV-AUTH-004: Defers localStorage writes until auth check is complete
 */
export function saveTheme(themeId: ThemeId, storageKey = THEME_STORAGE_KEY): void {
  const saveOperation = () => {
    try {
      localStorage.setItem(storageKey, themeId);
      if (import.meta.env.DEV) {
        console.log(`🎨 Theme "${themeId}" saved to localStorage after auth ready`);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        // Storage quota exceeded - silently continue, theme will still work
        if (import.meta.env.DEV) {
          console.warn('localStorage quota exceeded, theme preference not saved');
        }
      } else {
        console.warn('Failed to save theme to localStorage:', error);
      }
    }
  };

  // DV-AUTH-004: Gate theme persistence behind auth readiness
  if (isAuthReady()) {
    // Auth check complete, save immediately
    saveOperation();
  } else {
    // Defer until auth check is complete
    if (import.meta.env.DEV) {
      console.log(`🎨 Deferring theme "${themeId}" save until auth ready`);
    }
    whenAuthReady(saveOperation, `theme-save-${themeId}`);
  }
}

/**
 * Apply theme to document element with proper typing
 */
export function applyTheme(themeId: ThemeId): void {
  const root = document.documentElement;
  
  // Set data-theme attribute
  root.setAttribute('data-theme', themeId);
  
  // Dispatch custom event with proper typing
  const detail: ThemeChangeDetail = { themeId, timestamp: Date.now() };
  const event = new CustomEvent<ThemeChangeDetail>(THEME_CHANGE_EVENT, { detail });
  document.dispatchEvent(event);
  
  if (import.meta.env.DEV) {
    console.log(`🎨 Theme applied: ${themeId}`);
  }
}